import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Mail, 
  RefreshCw, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  Search,
  X,
  Calendar,
  User,
  LogOut,
  Euro,
  Save,
  Trash2,
  FileText,
  Sparkles,
  Plus,
  Check,
  Edit3,
  Loader,
  Copy,
  AlertCircle,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import SharedPageHeader from '../components/layout/PageHeader';

const PageContainer = styled.div`
  padding: 20px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  border: 1px solid rgba(165, 145, 239, 0.6);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(165, 145, 239, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  }

  &:hover {
    background: linear-gradient(135deg, var(--secondary), var(--accent));
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(165, 145, 239, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    animation: ${props => props.$loading ? 'spin 1s linear infinite' : 'none'};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 15px;
  margin-bottom: 25px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  background: linear-gradient(135deg, var(--bg) 0%, var(--bg) 100%);
  border-radius: 10px;
  padding: 4px;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(165, 145, 239, 0.3);
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: ${props => props.$active ? 'linear-gradient(135deg, var(--primary), var(--secondary))' : 'transparent'};
  color: ${props => props.$active ? 'var(--btn-text)' : 'var(--text-secondary)'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.$active ? '0 4px 12px rgba(165, 145, 239, 0.3)' : 'none'};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${props => props.$active ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)' : 'none'};
  }

  &:hover {
    background: ${props => props.$active ? 'linear-gradient(135deg, var(--secondary), var(--accent))' : 'rgba(165, 145, 239, 0.1)'};
    color: var(--text-primary);
  }

  svg {
    opacity: ${props => props.$active ? 1 : 0.7};
  }
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg) 100%);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(165, 145, 239, 0.3);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(165, 145, 239, 0.5), transparent);
  }

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(165, 145, 239, 0.6);
    box-shadow: 0 8px 24px rgba(165, 145, 239, 0.2), 0 0 0 1px rgba(165, 145, 239, 0.3);
  }
`;

const StockValueCard = styled(StatCard)`
  background: linear-gradient(135deg, var(--card-bg) 0%, #1a472a 100%);
  border-color: var(--success);
`;

const StockValue = styled.div`
  font-size: 28px;
  font-weight: 900;
  color: var(--success);
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const StatIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${props => props.$color || 'var(--primary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 900;
  color: var(--text-primary);
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 10px 14px;
  flex: 1;
  max-width: 400px;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.2);
  }

  input {
    background: transparent;
    border: none;
    color: var(--text-primary);
    font-size: 14px;
    width: 100%;
    outline: none;

    &::placeholder {
      color: var(--text-muted);
    }
  }

  svg {
    color: var(--text-muted);
  }
`;

const FilterDropdown = styled.select`
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 10px 14px;
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: var(--primary);
  }

  option {
    background: var(--bg);
    color: var(--text-primary);
  }
`;

const ClearFiltersButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: transparent;
  border: 1px solid var(--card-border);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--card-border);
    color: var(--text-primary);
  }
`;

const TableContainer = styled.div`
  background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg) 100%);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(165, 145, 239, 0.4);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, rgba(165, 145, 239, 0.6), transparent);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: linear-gradient(135deg, var(--bg) 0%, var(--bg) 100%);
  box-shadow: 0 2px 8px rgba(165, 145, 239, 0.3);
`;

const TableHeaderCell = styled.th`
  padding: 14px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid rgba(165, 145, 239, 0.2);
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(165, 145, 239, 0.1);
    color: var(--text-primary);
  }

  svg {
    margin-left: 6px;
    vertical-align: middle;
  }
`;

const SortIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  color: ${props => props.$active ? 'var(--primary)' : 'var(--text-muted)'};
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(63, 65, 71, 0.5);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(165, 145, 239, 0.05);
    box-shadow: inset 0 0 20px rgba(165, 145, 239, 0.05);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 14px 16px;
  font-size: 14px;
  color: var(--text-secondary);
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmailCell = styled(TableCell)`
  color: var(--primary);
  font-weight: 600;
`;

const DeviceCell = styled(TableCell)`
  color: var(--warning);
  font-weight: 500;
`;

const DateCell = styled(TableCell)`
  color: var(--text-muted);
  font-size: 13px;
`;

const InlineInput = styled.input`
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 4px 6px;
  color: ${p => p.$highlight ? 'var(--primary)' : p.$mono ? 'var(--text-muted)' : 'var(--text-secondary)'};
  font-size: 13px;
  font-weight: ${p => p.$highlight ? 600 : 400};
  font-family: ${p => p.$mono ? "'Consolas', 'Monaco', monospace" : 'inherit'};
  width: 100%;
  outline: none;
  transition: all 0.15s ease;

  &:hover { border-color: var(--card-border); }
  &:focus { border-color: var(--primary); background: var(--bg); }
`;

const InlineSelect = styled.select`
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 4px 6px;
  color: var(--text-secondary);
  font-size: 13px;
  outline: none;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { border-color: var(--card-border); }
  &:focus { border-color: var(--primary); background: var(--bg); }

  option { background: var(--bg); color: var(--text-primary); }
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: ${p => p.$color || 'var(--text-muted)'};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: ${p => p.$color ? `${p.$color}18` : 'var(--surface)'};
    border-color: ${p => p.$color || 'var(--card-border)'};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--text-muted);

  svg {
    margin-bottom: 15px;
    opacity: 0.5;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-secondary);
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background: var(--bg);
  border-top: 1px solid var(--card-border);
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const PaginationButton = styled.button`
  padding: 8px 14px;
  background: ${props => props.$active ? 'var(--primary)' : 'transparent'};
  border: 1px solid ${props => props.$active ? 'var(--primary)' : 'var(--card-border)'};
  border-radius: 6px;
  color: ${props => props.$active ? 'var(--btn-text)' : 'var(--text-secondary)'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.$active ? 'var(--secondary)' : 'var(--card-border)'};
    color: var(--text-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Gmail = () => {
  const [accounts, setAccounts] = useState([]);
  const [disconnectedAccounts, setDisconnectedAccounts] = useState([]);
  const [devices, setDevices] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('created');
  const [editedRows, setEditedRows] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [pendingSearch, setPendingSearch] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateCount, setGenerateCount] = useState(10);
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [generatedLines, setGeneratedLines] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [editingGenerated, setEditingGenerated] = useState(false);
  const [editedGenerated, setEditedGenerated] = useState('');
  const [addingToFile, setAddingToFile] = useState(false);
  const [selectedPendingIds, setSelectedPendingIds] = useState(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [pendingEdits, setPendingEdits] = useState({});
  const [pendingDirty, setPendingDirty] = useState(false);
  const [savingPending, setSavingPending] = useState(false);
  const [createdFileAccounts, setCreatedFileAccounts] = useState([]);
  const [createdFileEdits, setCreatedFileEdits] = useState({});
  const [createdFileDirty, setCreatedFileDirty] = useState(false);
  const [savingCreatedFile, setSavingCreatedFile] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);
  const [newAccount, setNewAccount] = useState({ gmail_address: '', gmail_password: '', first_name: '', last_name: '', birth_date: '', gender: '' });
  const [addingAccount, setAddingAccount] = useState(false);
  const itemsPerPage = 20;
  const EMAIL_PRICE = 0.50;

  useEffect(() => {
    fetchGmailAccounts();
    fetchDisconnectedAccounts();
    fetchDevices();
    fetchPendingAccounts();
    fetchCreatedFileAccounts();
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await fetch('/api/devices');
      if (response.ok) {
        const data = await response.json();
        // Créer un mapping device_id -> device_name
        const deviceMap = {};
        (data.devices || data || []).forEach(device => {
          deviceMap[device.device_id] = device.device_name || device.device_id;
        });
        setDevices(deviceMap);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des devices:', error);
    }
  };

  const fetchPendingAccounts = async () => {
    try {
      const response = await fetch('/api/gmail-scripts/pending-accounts');
      if (response.ok) {
        const data = await response.json();
        setPendingAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Erreur chargement comptes en attente:', error);
    }
  };

  const fetchCreatedFileAccounts = async () => {
    try {
      const response = await fetch('/api/gmail-scripts/created-accounts');
      if (response.ok) {
        const data = await response.json();
        setCreatedFileAccounts(data.accounts || []);
        setCreatedFileEdits({});
        setCreatedFileDirty(false);
      }
    } catch (error) {
      console.error('Erreur chargement comptes créés (fichier):', error);
    }
  };

  const deletePendingAccount = async (dbId) => {
    try {
      const res = await fetch(`/api/gmail-scripts/pending-accounts/${dbId}`, { method: 'DELETE' });
      if (res.ok) fetchPendingAccounts();
    } catch (err) {
      console.error('Erreur suppression:', err);
    }
  };

  const togglePendingSelect = (id) => {
    setSelectedPendingIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllPending = () => {
    if (selectedPendingIds.size === filteredPending.length) {
      setSelectedPendingIds(new Set());
    } else {
      setSelectedPendingIds(new Set(filteredPending.map(a => a.id)));
    }
  };

  const bulkDeletePending = async () => {
    if (selectedPendingIds.size === 0) return;
    const count = selectedPendingIds.size;
    if (!window.confirm(`Supprimer ${count} compte(s) en attente ?`)) return;
    setDeletingBulk(true);
    try {
      const isAll = count === pendingAccounts.length;
      const res = await fetch('/api/gmail-scripts/pending-accounts-bulk', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isAll ? { all: true } : { ids: Array.from(selectedPendingIds) })
      });
      if (res.ok) {
        setSelectedPendingIds(new Set());
        fetchPendingAccounts();
      }
    } catch (err) {
      console.error('Erreur suppression groupée:', err);
    } finally {
      setDeletingBulk(false);
    }
  };

  // Pending inline edit helpers
  const updatePendingField = (index, field, value) => {
    setPendingEdits(prev => ({
      ...prev,
      [index]: { ...(prev[index] || {}), [field]: value }
    }));
    setPendingDirty(true);
  };

  const getPendingFieldValue = (account, field) => {
    if (pendingEdits[account.index] && pendingEdits[account.index][field] !== undefined) {
      return pendingEdits[account.index][field];
    }
    return account[field] || '';
  };

  const movePendingRow = (fromIdx, direction) => {
    const toIdx = fromIdx + direction;
    if (toIdx < 0 || toIdx >= pendingAccounts.length) return;
    const newList = [...pendingAccounts];
    [newList[fromIdx], newList[toIdx]] = [newList[toIdx], newList[fromIdx]];
    // Reindex
    newList.forEach((a, i) => { a.index = i; });
    setPendingAccounts(newList);
    // Swap edits too
    setPendingEdits(prev => {
      const n = { ...prev };
      const fromEdits = n[fromIdx];
      const toEdits = n[toIdx];
      if (fromEdits) n[toIdx] = fromEdits; else delete n[toIdx];
      if (toEdits) n[fromIdx] = toEdits; else delete n[fromIdx];
      return n;
    });
    setPendingDirty(true);
  };

  const savePendingAccounts = async () => {
    setSavingPending(true);
    try {
      // Build final list applying edits
      const final = pendingAccounts.map(a => {
        const edits = pendingEdits[a.index] || {};
        return {
          nom: edits.nom ?? a.nom,
          prenom: edits.prenom ?? a.prenom,
          sexe: edits.sexe ?? a.sexe,
          date_naissance: edits.date_naissance ?? a.date_naissance,
          email: edits.email ?? a.email,
          password: edits.password ?? a.password,
        };
      });
      const res = await fetch('/api/gmail-scripts/pending-accounts', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accounts: final })
      });
      if (res.ok) {
        setPendingEdits({});
        setPendingDirty(false);
        fetchPendingAccounts();
      }
    } catch (err) {
      console.error('Erreur sauvegarde pending:', err);
    } finally {
      setSavingPending(false);
    }
  };

  // Created file inline edit helpers
  const updateCreatedFileField = (index, field, value) => {
    setCreatedFileEdits(prev => ({
      ...prev,
      [index]: { ...(prev[index] || {}), [field]: value }
    }));
    setCreatedFileDirty(true);
  };

  const getCreatedFileFieldValue = (account, field) => {
    if (createdFileEdits[account.index] && createdFileEdits[account.index][field] !== undefined) {
      return createdFileEdits[account.index][field];
    }
    return account[field] || '';
  };

  const moveCreatedFileRow = (fromIdx, direction) => {
    const toIdx = fromIdx + direction;
    if (toIdx < 0 || toIdx >= createdFileAccounts.length) return;
    const newList = [...createdFileAccounts];
    [newList[fromIdx], newList[toIdx]] = [newList[toIdx], newList[fromIdx]];
    newList.forEach((a, i) => { a.index = i; });
    setCreatedFileAccounts(newList);
    setCreatedFileEdits(prev => {
      const n = { ...prev };
      const fromEdits = n[fromIdx];
      const toEdits = n[toIdx];
      if (fromEdits) n[toIdx] = fromEdits; else delete n[toIdx];
      if (toEdits) n[fromIdx] = toEdits; else delete n[fromIdx];
      return n;
    });
    setCreatedFileDirty(true);
  };

  const saveCreatedFileAccounts = async () => {
    setSavingCreatedFile(true);
    try {
      const final = createdFileAccounts.map(a => {
        const edits = createdFileEdits[a.index] || {};
        return {
          nom: edits.nom ?? a.nom,
          prenom: edits.prenom ?? a.prenom,
          sexe: edits.sexe ?? a.sexe,
          date_naissance: edits.date_naissance ?? a.date_naissance,
          email: edits.email ?? a.email,
          password: edits.password ?? a.password,
        };
      });
      const res = await fetch('/api/gmail-scripts/created-accounts', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accounts: final })
      });
      if (res.ok) {
        setCreatedFileEdits({});
        setCreatedFileDirty(false);
        fetchCreatedFileAccounts();
      }
    } catch (err) {
      console.error('Erreur sauvegarde created file:', err);
    } finally {
      setSavingCreatedFile(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGeneratedLines([]);
    try {
      const res = await fetch('/api/gmail-scripts/generate-accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: generateCount, prompt: generatePrompt, saveToDB: false })
      });
      const data = await res.json();
      if (data.success) {
        setGeneratedLines(data.lines);
        setEditedGenerated(data.lines.join('\n'));
        setEditingGenerated(false);
      } else {
        alert('Erreur: ' + (data.error || 'Génération échouée'));
      }
    } catch (err) {
      alert('Erreur réseau: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleAddAccount = async () => {
    if (!newAccount.gmail_address) return;
    setAddingAccount(true);
    try {
      const res = await fetch('/api/gmail-accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAccount)
      });
      const data = await res.json();
      if (data.success) {
        setNewAccount({ gmail_address: '', gmail_password: '', first_name: '', last_name: '', birth_date: '', gender: '' });
        setShowAddRow(false);
        fetchGmailAccounts();
      } else {
        alert(data.error || 'Erreur lors de l\'ajout');
      }
    } catch (err) {
      alert('Erreur réseau: ' + err.message);
    } finally {
      setAddingAccount(false);
    }
  };

  const handleAddToFile = async () => {
    const lines = editingGenerated
      ? editedGenerated.split('\n').filter(l => l.trim() && l.split(';').length >= 6)
      : generatedLines;
    if (lines.length === 0) return;
    setAddingToFile(true);
    try {
      const res = await fetch('/api/gmail-scripts/pending-accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lines })
      });
      const data = await res.json();
      if (data.success) {
        setShowGenerateModal(false);
        setGeneratedLines([]);
        setEditedGenerated('');
        fetchPendingAccounts();
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setAddingToFile(false);
    }
  };

  const updateField = (id, field, value) => {
    setEditedRows(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value }
    }));
  };

  const saveAccount = async (account) => {
    const changes = editedRows[account.id];
    if (!changes || Object.keys(changes).length === 0) return;
    setSavingId(account.id);
    try {
      const payload = { ...account, ...changes };
      const res = await fetch(`/api/gmail-accounts/${account.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        setAccounts(prev => prev.map(a => a.id === account.id ? data.account : a));
        setEditedRows(prev => { const n = { ...prev }; delete n[account.id]; return n; });
      }
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
    } finally {
      setSavingId(null);
    }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm('Supprimer ce compte Gmail ?')) return;
    try {
      const res = await fetch(`/api/gmail-accounts/${id}`, { method: 'DELETE' });
      if (res.ok) setAccounts(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error('Erreur suppression:', err);
    }
  };

  const getFieldValue = (account, field) => {
    if (editedRows[account.id] && editedRows[account.id][field] !== undefined) {
      return editedRows[account.id][field];
    }
    return account[field] || '';
  };

  const fetchGmailAccounts = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/gmail-accounts');
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des comptes Gmail:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDisconnectedAccounts = async () => {
    try {
      const response = await fetch('/api/disconnected-gmail-accounts');
      if (response.ok) {
        const data = await response.json();
        setDisconnectedAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des comptes déconnectés:', error);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <SortIcon><ArrowUpDown size={14} /></SortIcon>;
    }
    return sortConfig.direction === 'asc' 
      ? <SortIcon $active><ArrowUp size={14} /></SortIcon>
      : <SortIcon $active><ArrowDown size={14} /></SortIcon>;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setGenderFilter('all');
    setCurrentPage(1);
  };

  // Filtrage
  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = searchTerm === '' || 
      account.gmail_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.device_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesGender = genderFilter === 'all' || account.gender === genderFilter;
    
    return matchesSearch && matchesGender;
  });

  // Tri
  const sortedAccounts = [...filteredAccounts].sort((a, b) => {
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Gestion des valeurs null/undefined
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    // Tri pour les dates
    if (sortConfig.key === 'created_at' || sortConfig.key === 'updated_at') {
      aValue = new Date(aValue || 0);
      bValue = new Date(bValue || 0);
    } else if (sortConfig.key === 'birth_date') {
      aValue = parseDateDMY(aValue) || new Date(0);
      bValue = parseDateDMY(bValue) || new Date(0);
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedAccounts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAccounts = sortedAccounts.slice(startIndex, startIndex + itemsPerPage);

  // Stats
  const stats = {
    total: accounts.length,
    male: accounts.filter(a => a.gender === 'M').length,
    female: accounts.filter(a => a.gender === 'F').length,
    today: accounts.filter(a => {
      const created = new Date(a.created_at);
      const today = new Date();
      return created.toDateString() === today.toDateString();
    }).length,
    stockValue: (accounts.length * EMAIL_PRICE).toFixed(2),
    disconnectedCount: disconnectedAccounts.length
  };

  // Helper pour obtenir le device_name à partir du device_id
  const getDeviceName = (deviceId) => {
    return devices[deviceId] || deviceId || '-';
  };

  // Gestion du refresh selon l'onglet actif
  const handleRefresh = () => {
    if (activeTab === 'created') {
      fetchGmailAccounts();
    } else if (activeTab === 'pending') {
      fetchPendingAccounts();
    } else if (activeTab === 'created_file') {
      fetchCreatedFileAccounts();
    } else {
      fetchDisconnectedAccounts();
    }
    fetchDevices();
  };

  const filteredPending = pendingAccounts.filter(a => {
    if (!pendingSearch) return true;
    const s = pendingSearch.toLowerCase();
    return (a.nom + ' ' + a.prenom + ' ' + a.email + ' ' + a.sexe).toLowerCase().includes(s);
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseDateDMY = (dateString) => {
    if (!dateString) return null;
    // Handle DD/MM/YYYY format from DB
    const m = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) return new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? null : d;
  };

  return (
    <PageContainer>
      <SharedPageHeader icon={Mail} title="Gmail Accounts" subtitle="Gestion des comptes Gmail">
        <HeaderActions>
          <RefreshButton onClick={handleRefresh} $loading={loading} disabled={loading}>
            <RefreshCw size={18} />
            {loading ? 'Chargement...' : 'Actualiser'}
          </RefreshButton>
        </HeaderActions>
      </SharedPageHeader>

      <StatsContainer>
        <StatCard>
          <StatHeader>
            <StatIcon $color="var(--primary)">
              <Mail size={18} />
            </StatIcon>
            <StatLabel>Total</StatLabel>
          </StatHeader>
          <StatValue>{stats.total}</StatValue>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon $color="var(--primary)">
              <User size={18} />
            </StatIcon>
            <StatLabel>Hommes</StatLabel>
          </StatHeader>
          <StatValue>{stats.male}</StatValue>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon $color="#eb459e">
              <User size={18} />
            </StatIcon>
            <StatLabel>Femmes</StatLabel>
          </StatHeader>
          <StatValue>{stats.female}</StatValue>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon $color="var(--success)">
              <Calendar size={18} />
            </StatIcon>
            <StatLabel>Aujourd'hui</StatLabel>
          </StatHeader>
          <StatValue>{stats.today}</StatValue>
        </StatCard>

        <StockValueCard>
          <StatHeader>
            <StatIcon $color="var(--success)">
              <Euro size={18} />
            </StatIcon>
            <StatLabel>Valeur Stock</StatLabel>
          </StatHeader>
          <StockValue>{stats.stockValue} €</StockValue>
        </StockValueCard>
      </StatsContainer>

      <TabContainer>
        <Tab 
          $active={activeTab === 'created'} 
          onClick={() => { setActiveTab('created'); setCurrentPage(1); }}
        >
          <Mail size={18} />
          Comptes créés ({stats.total})
        </Tab>
        <Tab 
          $active={activeTab === 'pending'} 
          onClick={() => { setActiveTab('pending'); setCurrentPage(1); fetchPendingAccounts(); }}
        >
          <FileText size={18} />
          En attente ({pendingAccounts.length})
        </Tab>
        <Tab 
          $active={activeTab === 'created_file'} 
          onClick={() => { setActiveTab('created_file'); setCurrentPage(1); fetchCreatedFileAccounts(); }}
        >
          <Check size={18} />
          Créés fichier ({createdFileAccounts.length})
        </Tab>
        <Tab 
          $active={activeTab === 'disconnected'} 
          onClick={() => { setActiveTab('disconnected'); setCurrentPage(1); }}
        >
          <LogOut size={18} />
          Comptes déconnectés ({stats.disconnectedCount})
        </Tab>
      </TabContainer>

      <FilterSection>
        <SearchInput>
          <Search size={18} />
          <input
            type="text"
            placeholder="Rechercher par email, nom, prénom ou device..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </SearchInput>

        <FilterDropdown
          value={genderFilter}
          onChange={(e) => {
            setGenderFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="all">Tous les genres</option>
          <option value="M">Hommes</option>
          <option value="F">Femmes</option>
        </FilterDropdown>

        {(searchTerm || genderFilter !== 'all') && (
          <ClearFiltersButton onClick={clearFilters}>
            <X size={16} />
            Effacer les filtres
          </ClearFiltersButton>
        )}
      </FilterSection>

      <TableContainer>
        {activeTab === 'created' ? (
          <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--card-border)' }}>
            <RefreshButton onClick={() => setShowAddRow(!showAddRow)} style={{ background: showAddRow ? 'var(--error)' : 'linear-gradient(135deg, #22c55e, #16a34a)', borderColor: showAddRow ? 'rgba(239,68,68,0.6)' : 'rgba(34,197,94,0.6)', boxShadow: showAddRow ? '0 4px 12px rgba(239,68,68,0.3)' : '0 4px 12px rgba(34,197,94,0.3)' }}>
              {showAddRow ? <X size={16} /> : <Plus size={16} />}
              {showAddRow ? 'Annuler' : 'Ajouter un compte'}
            </RefreshButton>
          </div>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell style={{ width: 50 }} onClick={() => handleSort('id')}>
                  ID {getSortIcon('id')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('device_id')}>
                  Device {getSortIcon('device_id')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('gmail_address')}>
                  Email {getSortIcon('gmail_address')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('gmail_password')}>
                  Mot de passe {getSortIcon('gmail_password')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('first_name')}>
                  Prénom {getSortIcon('first_name')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('last_name')}>
                  Nom {getSortIcon('last_name')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('birth_date')}>
                  Naissance {getSortIcon('birth_date')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('gender')}>
                  Genre {getSortIcon('gender')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('created_at')}>
                  Créé le {getSortIcon('created_at')}
                </TableHeaderCell>
                <TableHeaderCell style={{ width: 90, textAlign: 'center' }}>Actions</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {showAddRow && (
                <TableRow style={{ background: 'rgba(34,197,94,0.06)' }}>
                  <TableCell style={{ textAlign: 'center', color: 'var(--success)', fontWeight: 700 }}>+</TableCell>
                  <TableCell style={{ padding: '6px 8px', color: 'var(--text-muted)', fontSize: 11 }}>—</TableCell>
                  <TableCell style={{ padding: '6px 8px' }}>
                    <InlineInput value={newAccount.gmail_address} onChange={e => setNewAccount(p => ({ ...p, gmail_address: e.target.value }))} placeholder="email@gmail.com" $highlight autoFocus />
                  </TableCell>
                  <TableCell style={{ padding: '6px 8px' }}>
                    <InlineInput value={newAccount.gmail_password} onChange={e => setNewAccount(p => ({ ...p, gmail_password: e.target.value }))} placeholder="Mot de passe" $mono />
                  </TableCell>
                  <TableCell style={{ padding: '6px 8px' }}>
                    <InlineInput value={newAccount.first_name} onChange={e => setNewAccount(p => ({ ...p, first_name: e.target.value }))} placeholder="Prénom" />
                  </TableCell>
                  <TableCell style={{ padding: '6px 8px' }}>
                    <InlineInput value={newAccount.last_name} onChange={e => setNewAccount(p => ({ ...p, last_name: e.target.value }))} placeholder="Nom" />
                  </TableCell>
                  <TableCell style={{ padding: '6px 8px' }}>
                    <InlineInput value={newAccount.birth_date} onChange={e => setNewAccount(p => ({ ...p, birth_date: e.target.value }))} placeholder="JJ/MM/AAAA" style={{ width: 100 }} />
                  </TableCell>
                  <TableCell style={{ padding: '6px 8px' }}>
                    <InlineSelect value={newAccount.gender} onChange={e => setNewAccount(p => ({ ...p, gender: e.target.value }))}>
                      <option value="">-</option>
                      <option value="M">Homme</option>
                      <option value="F">Femme</option>
                    </InlineSelect>
                  </TableCell>
                  <TableCell style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</TableCell>
                  <TableCell style={{ padding: '6px 8px', textAlign: 'center' }}>
                    <ActionBtn $color="var(--success)" onClick={handleAddAccount} disabled={!newAccount.gmail_address || addingAccount} title="Ajouter">
                      {addingAccount ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={14} />}
                    </ActionBtn>
                  </TableCell>
                </TableRow>
              )}
              {paginatedAccounts.length > 0 ? (
                paginatedAccounts.map((account) => {
                  const hasChanges = editedRows[account.id] && Object.keys(editedRows[account.id]).length > 0;
                  const isSaving = savingId === account.id;
                  return (
                    <TableRow key={account.id}>
                      <TableCell>{account.id}</TableCell>
                      <DeviceCell>{getDeviceName(account.device_id)}</DeviceCell>
                      <TableCell style={{ padding: '6px 8px' }}>
                        <InlineInput value={getFieldValue(account, 'gmail_address')} onChange={e => updateField(account.id, 'gmail_address', e.target.value)} $highlight />
                      </TableCell>
                      <TableCell style={{ padding: '6px 8px' }}>
                        <InlineInput value={getFieldValue(account, 'gmail_password')} onChange={e => updateField(account.id, 'gmail_password', e.target.value)} $mono />
                      </TableCell>
                      <TableCell style={{ padding: '6px 8px' }}>
                        <InlineInput value={getFieldValue(account, 'first_name')} onChange={e => updateField(account.id, 'first_name', e.target.value)} />
                      </TableCell>
                      <TableCell style={{ padding: '6px 8px' }}>
                        <InlineInput value={getFieldValue(account, 'last_name')} onChange={e => updateField(account.id, 'last_name', e.target.value)} />
                      </TableCell>
                      <TableCell style={{ padding: '6px 8px' }}>
                        <InlineInput value={getFieldValue(account, 'birth_date')} onChange={e => updateField(account.id, 'birth_date', e.target.value)} placeholder="DD/MM/YYYY" style={{ width: 100 }} />
                      </TableCell>
                      <TableCell style={{ padding: '6px 8px' }}>
                        <InlineSelect value={getFieldValue(account, 'gender')} onChange={e => updateField(account.id, 'gender', e.target.value)}>
                          <option value="">-</option>
                          <option value="M">Homme</option>
                          <option value="F">Femme</option>
                        </InlineSelect>
                      </TableCell>
                      <DateCell>{formatDate(account.created_at)}</DateCell>
                      <TableCell style={{ padding: '6px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <ActionBtn $color="var(--success)" onClick={() => saveAccount(account)} disabled={!hasChanges || isSaving} title="Enregistrer">
                          <Save size={14} />
                        </ActionBtn>
                        <ActionBtn $color="var(--error)" onClick={() => deleteAccount(account.id)} title="Supprimer" style={{ marginLeft: 4 }}>
                          <Trash2 size={14} />
                        </ActionBtn>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <tr>
                  <TableCell colSpan={10}>
                    <EmptyState>
                      <Mail size={48} />
                      <h3>Aucun compte Gmail trouvé</h3>
                      <p>
                        {searchTerm || genderFilter !== 'all' 
                          ? 'Essayez de modifier vos filtres de recherche'
                          : 'Les comptes Gmail apparaîtront ici une fois créés'}
                      </p>
                    </EmptyState>
                  </TableCell>
                </tr>
              )}
            </TableBody>
          </Table>
          </>
        ) : activeTab === 'pending' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--card-border)' }}>
              <SearchInput style={{ flex: 1, maxWidth: 350 }}>
                <Search size={16} />
                <input placeholder="Rechercher dans les comptes en attente..." value={pendingSearch} onChange={e => setPendingSearch(e.target.value)} />
              </SearchInput>
              <RefreshButton onClick={() => setShowGenerateModal(true)} style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', borderColor: 'rgba(34,197,94,0.6)', boxShadow: '0 4px 12px rgba(34,197,94,0.3)' }}>
                <Sparkles size={16} />
                Générer avec IA
              </RefreshButton>
              {selectedPendingIds.size > 0 && (
                <RefreshButton onClick={bulkDeletePending} disabled={deletingBulk}
                  style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', borderColor: 'rgba(239,68,68,0.6)', boxShadow: '0 4px 12px rgba(239,68,68,0.3)' }}>
                  {deletingBulk ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={16} />}
                  Supprimer ({selectedPendingIds.size})
                </RefreshButton>
              )}
              {pendingDirty && (
                <RefreshButton onClick={savePendingAccounts} disabled={savingPending}
                  style={{ background: 'linear-gradient(135deg, var(--primary), var(--secondary))', borderColor: 'rgba(88,101,242,0.6)' }}>
                  {savingPending ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                  Sauvegarder
                </RefreshButton>
              )}
              <RefreshButton onClick={fetchPendingAccounts} $loading={loading}>
                <RefreshCw size={16} />
              </RefreshButton>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 16px', borderBottom: '1px solid var(--card-border)' }}>
              <FileText size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
              Table: <code style={{ background: 'var(--bg)', padding: '2px 6px', borderRadius: 4 }}>gmail_pending_accounts</code>
              <span style={{ marginLeft: 12 }}>{filteredPending.length} comptes en attente de création</span>
              {pendingDirty && <span style={{ marginLeft: 12, color: '#f59e0b', fontWeight: 700 }}>● Modifications non sauvegardées</span>}
            </div>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell style={{ width: 36, textAlign: 'center' }}>
                    <input type="checkbox" checked={filteredPending.length > 0 && selectedPendingIds.size === filteredPending.length} onChange={toggleSelectAllPending} style={{ cursor: 'pointer', accentColor: 'var(--primary)' }} />
                  </TableHeaderCell>
                  <TableHeaderCell style={{ width: 60 }}>Ordre</TableHeaderCell>
                  <TableHeaderCell>Nom</TableHeaderCell>
                  <TableHeaderCell>Prénom</TableHeaderCell>
                  <TableHeaderCell style={{ width: 70 }}>Sexe</TableHeaderCell>
                  <TableHeaderCell>Naissance</TableHeaderCell>
                  <TableHeaderCell>Email</TableHeaderCell>
                  <TableHeaderCell>Mot de passe</TableHeaderCell>
                  <TableHeaderCell style={{ width: 50 }}>Suppr.</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {filteredPending.length > 0 ? (
                  filteredPending.map((a) => (
                    <TableRow key={a.id} style={{ background: selectedPendingIds.has(a.id) ? 'rgba(165,145,239,0.08)' : undefined }}>
                      <TableCell style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <input type="checkbox" checked={selectedPendingIds.has(a.id)} onChange={() => togglePendingSelect(a.id)} style={{ cursor: 'pointer', accentColor: 'var(--primary)' }} />
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                        <ActionBtn $color="var(--text-muted)" onClick={() => movePendingRow(a.index, -1)} disabled={a.index === 0} title="Monter">
                          <ChevronUp size={13} />
                        </ActionBtn>
                        <ActionBtn $color="var(--text-muted)" onClick={() => movePendingRow(a.index, 1)} disabled={a.index === pendingAccounts.length - 1} title="Descendre">
                          <ChevronDown size={13} />
                        </ActionBtn>
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px' }}>
                        <InlineInput value={getPendingFieldValue(a, 'nom')} onChange={e => updatePendingField(a.index, 'nom', e.target.value)} style={{ fontWeight: 600 }} />
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px' }}>
                        <InlineInput value={getPendingFieldValue(a, 'prenom')} onChange={e => updatePendingField(a.index, 'prenom', e.target.value)} />
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px' }}>
                        <InlineSelect value={getPendingFieldValue(a, 'sexe')} onChange={e => updatePendingField(a.index, 'sexe', e.target.value)}>
                          <option value="">-</option>
                          <option value="M">M</option>
                          <option value="F">F</option>
                        </InlineSelect>
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px' }}>
                        <InlineInput value={getPendingFieldValue(a, 'date_naissance')} onChange={e => updatePendingField(a.index, 'date_naissance', e.target.value)} placeholder="JJ/MM/AAAA" $mono />
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px' }}>
                        <InlineInput value={getPendingFieldValue(a, 'email')} onChange={e => updatePendingField(a.index, 'email', e.target.value)} $highlight />
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px' }}>
                        <InlineInput value={getPendingFieldValue(a, 'password')} onChange={e => updatePendingField(a.index, 'password', e.target.value)} $mono />
                      </TableCell>
                      <TableCell style={{ textAlign: 'center', padding: '4px 6px' }}>
                        <ActionBtn $color="var(--error)" onClick={() => deletePendingAccount(a.id)} title="Supprimer">
                          <Trash2 size={13} />
                        </ActionBtn>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <tr>
                    <TableCell colSpan={9}>
                      <EmptyState>
                        <FileText size={48} />
                        <h3>Aucun compte en attente</h3>
                        <p>Utilisez le bouton "Générer avec IA" pour créer des comptes</p>
                      </EmptyState>
                    </TableCell>
                  </tr>
                )}
              </TableBody>
            </Table>
          </>
        ) : activeTab === 'created_file' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--card-border)' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>
                <FileText size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
                Fichier: <code style={{ background: 'var(--bg)', padding: '2px 6px', borderRadius: 4 }}>qlickr/google/mail-list-created.txt</code>
                <span style={{ marginLeft: 12 }}>{createdFileAccounts.length} comptes créés</span>
                {createdFileDirty && <span style={{ marginLeft: 12, color: '#f59e0b', fontWeight: 700 }}>● Modifications non sauvegardées</span>}
              </div>
              {createdFileDirty && (
                <RefreshButton onClick={saveCreatedFileAccounts} disabled={savingCreatedFile}
                  style={{ background: 'linear-gradient(135deg, var(--primary), var(--secondary))', borderColor: 'rgba(88,101,242,0.6)' }}>
                  {savingCreatedFile ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                  Sauvegarder
                </RefreshButton>
              )}
              <RefreshButton onClick={fetchCreatedFileAccounts} $loading={loading}>
                <RefreshCw size={16} />
              </RefreshButton>
            </div>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell style={{ width: 60 }}>Ordre</TableHeaderCell>
                  <TableHeaderCell>Nom</TableHeaderCell>
                  <TableHeaderCell>Prénom</TableHeaderCell>
                  <TableHeaderCell style={{ width: 70 }}>Sexe</TableHeaderCell>
                  <TableHeaderCell>Naissance</TableHeaderCell>
                  <TableHeaderCell>Email</TableHeaderCell>
                  <TableHeaderCell>Mot de passe</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {createdFileAccounts.length > 0 ? (
                  createdFileAccounts.map((a) => (
                    <TableRow key={a.index}>
                      <TableCell style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                        <ActionBtn $color="var(--text-muted)" onClick={() => moveCreatedFileRow(a.index, -1)} disabled={a.index === 0} title="Monter">
                          <ChevronUp size={13} />
                        </ActionBtn>
                        <ActionBtn $color="var(--text-muted)" onClick={() => moveCreatedFileRow(a.index, 1)} disabled={a.index === createdFileAccounts.length - 1} title="Descendre">
                          <ChevronDown size={13} />
                        </ActionBtn>
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px' }}>
                        <InlineInput value={getCreatedFileFieldValue(a, 'nom')} onChange={e => updateCreatedFileField(a.index, 'nom', e.target.value)} style={{ fontWeight: 600 }} />
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px' }}>
                        <InlineInput value={getCreatedFileFieldValue(a, 'prenom')} onChange={e => updateCreatedFileField(a.index, 'prenom', e.target.value)} />
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px' }}>
                        <InlineSelect value={getCreatedFileFieldValue(a, 'sexe')} onChange={e => updateCreatedFileField(a.index, 'sexe', e.target.value)}>
                          <option value="">-</option>
                          <option value="M">M</option>
                          <option value="F">F</option>
                        </InlineSelect>
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px' }}>
                        <InlineInput value={getCreatedFileFieldValue(a, 'date_naissance')} onChange={e => updateCreatedFileField(a.index, 'date_naissance', e.target.value)} $mono />
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px' }}>
                        <InlineInput value={getCreatedFileFieldValue(a, 'email')} onChange={e => updateCreatedFileField(a.index, 'email', e.target.value)} $highlight />
                      </TableCell>
                      <TableCell style={{ padding: '4px 6px' }}>
                        <InlineInput value={getCreatedFileFieldValue(a, 'password')} onChange={e => updateCreatedFileField(a.index, 'password', e.target.value)} $mono />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <tr>
                    <TableCell colSpan={7}>
                      <EmptyState>
                        <Check size={48} />
                        <h3>Aucun compte créé dans le fichier</h3>
                        <p>Les comptes créés par le script apparaîtront ici</p>
                      </EmptyState>
                    </TableCell>
                  </tr>
                )}
              </TableBody>
            </Table>
          </>
        ) : (
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>ID</TableHeaderCell>
                <TableHeaderCell>Device</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Déconnecté le</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {disconnectedAccounts.length > 0 ? (
                disconnectedAccounts.map((account, index) => (
                  <TableRow key={account.id || index}>
                    <TableCell>{account.id || index + 1}</TableCell>
                    <DeviceCell>{account.device_name || getDeviceName(account.device_id)}</DeviceCell>
                    <EmailCell>{account.email}</EmailCell>
                    <DateCell>{formatDate(account.disconnected_at)}</DateCell>
                  </TableRow>
                ))
              ) : (
                <tr>
                  <TableCell colSpan={4}>
                    <EmptyState>
                      <LogOut size={48} />
                      <h3>Aucun compte déconnecté</h3>
                      <p>Les comptes Gmail déconnectés apparaîtront ici</p>
                    </EmptyState>
                  </TableCell>
                </tr>
              )}
            </TableBody>
          </Table>
        )}

        {activeTab === 'created' && sortedAccounts.length > itemsPerPage && (
          <Pagination>
            <PaginationInfo>
              Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, sortedAccounts.length)} sur {sortedAccounts.length} comptes
            </PaginationInfo>
            <PaginationButtons>
              <PaginationButton
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                {'<<'}
              </PaginationButton>
              <PaginationButton
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                {'<'}
              </PaginationButton>
              
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <PaginationButton
                    key={pageNum}
                    $active={currentPage === pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </PaginationButton>
                );
              })}
              
              <PaginationButton
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                {'>'}
              </PaginationButton>
              <PaginationButton
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                {'>>'}
              </PaginationButton>
            </PaginationButtons>
          </Pagination>
        )}
      </TableContainer>

      {/* ── Generate Accounts Modal ── */}
      {showGenerateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }} onClick={() => setShowGenerateModal(false)}>
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 16, width: 720, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px rgba(0,0,0,.4)' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #22c55e, #16a34a)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                  <Sparkles size={18} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>Générer des comptes Gmail</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Gemini AI - gemini-2.5-flash-lite</div>
                </div>
              </div>
              <button onClick={() => setShowGenerateModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><X size={20} /></button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              {/* Pre-prompt info */}
              <div style={{ background: 'rgba(59,130,246,.06)', border: '1px solid rgba(59,130,246,.15)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
                <div style={{ fontWeight: 700, color: '#3b82f6', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><AlertCircle size={14} /> Format de données</div>
                <code style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--text-primary)', background: 'var(--bg)', padding: '6px 10px', borderRadius: 6, marginTop: 4 }}>
                  Nom;Prénom;Sexe;Date de naissance;Adresse mail;Mot de passe
                </code>
              </div>

              {/* Count */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                <div style={{ flex: '0 0 180px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Nombre de lignes</label>
                  <input type="number" min={1} max={50} value={generateCount} onChange={e => setGenerateCount(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
                    style={{ width: '100%', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, outline: 'none' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                    Instructions personnalisées <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optionnel)</span>
                  </label>
                  <textarea value={generatePrompt} onChange={e => setGeneratePrompt(e.target.value)}
                    placeholder="Ex: Noms français uniquement, mots de passe avec majuscule + chiffre + symbole, emails en format prenom.nom.annee@gmail.com, personnes de 25-35 ans..."
                    style={{ width: '100%', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, outline: 'none', resize: 'vertical', minHeight: 70, fontFamily: 'inherit' }} />
                </div>
              </div>

              {/* Generate button */}
              <button onClick={handleGenerate} disabled={generating}
                style={{ width: '100%', padding: '12px', background: generating ? 'var(--card-border)' : 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: generating ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16, transition: 'all .2s' }}>
                {generating ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Génération en cours...</> : <><Sparkles size={16} /> Générer {generateCount} comptes</>}
              </button>

              {/* Generated results */}
              {generatedLines.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                      <Check size={14} style={{ verticalAlign: -2, marginRight: 4, color: '#22c55e' }} />
                      {generatedLines.length} comptes générés
                    </span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => { setEditingGenerated(!editingGenerated); if (!editingGenerated) setEditedGenerated(generatedLines.join('\n')); }}
                        style={{ padding: '4px 10px', background: editingGenerated ? 'var(--primary)' : 'var(--bg)', border: '1px solid var(--card-border)', borderRadius: 6, color: editingGenerated ? 'white' : 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Edit3 size={12} /> {editingGenerated ? 'Mode édition' : 'Modifier'}
                      </button>
                      <button onClick={() => { navigator.clipboard.writeText(generatedLines.join('\n')); }}
                        style={{ padding: '4px 10px', background: 'var(--bg)', border: '1px solid var(--card-border)', borderRadius: 6, color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Copy size={12} /> Copier
                      </button>
                    </div>
                  </div>

                  {editingGenerated ? (
                    <textarea value={editedGenerated} onChange={e => setEditedGenerated(e.target.value)}
                      style={{ width: '100%', minHeight: 200, maxHeight: 300, padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 11, fontFamily: "'JetBrains Mono', monospace", outline: 'none', resize: 'vertical', lineHeight: 1.6 }} />
                  ) : (
                    <div style={{ background: 'var(--bg)', border: '1px solid var(--card-border)', borderRadius: 8, maxHeight: 300, overflowY: 'auto', fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
                      {generatedLines.map((line, i) => {
                        const parts = line.split(';');
                        return (
                          <div key={i} style={{ padding: '6px 12px', borderBottom: '1px solid var(--card-border)', display: 'grid', gridTemplateColumns: '1fr 1fr 30px 80px 2fr 1.2fr', gap: 8, alignItems: 'center' }}>
                            <span style={{ fontWeight: 600 }}>{parts[0]}</span>
                            <span>{parts[1]}</span>
                            <span style={{ padding: '1px 4px', borderRadius: 4, fontSize: 10, fontWeight: 700, textAlign: 'center', background: parts[2] === 'M' ? 'rgba(59,130,246,.15)' : 'rgba(236,72,153,.15)', color: parts[2] === 'M' ? '#3b82f6' : '#ec4899' }}>{parts[2]}</span>
                            <span style={{ color: 'var(--text-muted)' }}>{parts[3]}</span>
                            <span style={{ color: '#22c55e' }}>{parts[4]}</span>
                            <span style={{ color: 'var(--text-muted)' }}>{parts[5]}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {generatedLines.length > 0 && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid var(--card-border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button onClick={() => { setGeneratedLines([]); setEditedGenerated(''); }}
                  style={{ padding: '10px 20px', background: 'var(--bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Annuler
                </button>
                <button onClick={handleAddToFile} disabled={addingToFile}
                  style={{ padding: '10px 24px', background: 'linear-gradient(135deg, var(--primary), var(--secondary))', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {addingToFile ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={14} />}
                  Ajouter en BDD ({editingGenerated ? editedGenerated.split('\n').filter(l => l.trim() && l.split(';').length >= 6).length : generatedLines.length} lignes)
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default Gmail;
